import { useState } from "react";

const readValue = <T,>(key: string, initialValue: T): T => {
  try {
    const item = window.localStorage.getItem(key);
    return item !== null ? (JSON.parse(item) as T) : initialValue;
  } catch {
    return initialValue;
  }
};

// Persists state to localStorage under `key`. Pass `null` as the key to disable persistence
// (state then behaves like a plain useState) without breaking the rules of hooks.
const useLocalStorage = <T,>(key: string | null, initialValue: T) => {
  const [value, setValue] = useState<T>(() => (key ? readValue(key, initialValue) : initialValue));

  const setStoredValue = (newValue: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const next = newValue instanceof Function ? newValue(prev) : newValue;
      if (key) {
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // ignore write errors (e.g. storage disabled or full)
        }
      }
      return next;
    });
  };

  return [value, setStoredValue] as const;
};

export default useLocalStorage;
